import { useEffect, useState, useCallback } from "react";
import {
  Check,
  FileText,
  HandHeart,
  Loader2,
  MessageCircle,
  Receipt,
  Trash2,
  X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { API, formatApiError } from "@/lib/api";
import { brl, formatDateTime } from "@/lib/format";

const STATUS_STYLE = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  confirmado: "bg-emerald-100 text-emerald-800 border-emerald-200",
  recusado: "bg-rose-100 text-rose-800 border-rose-200",
};

const STATUS_LABEL = { pendente: "PENDENTE", confirmado: "CONFIRMADO", recusado: "RECUSADO" };

export default function ContributionsPanel({ onChanged }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/contribuicoes", {
        params: filter ? { status: filter } : {},
      });
      setItems(res.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const enviarRecibo = async (c) => {
    try {
      const r = await api.get(`/admin/contribuicoes/${c.id}/recibo`);
      const link = `${process.env.REACT_APP_BACKEND_URL}${r.data.url_publica}`;
      const primeiro = (c.nome || "").trim().split(" ")[0];
      const texto = `Olá${primeiro ? `, ${primeiro}` : ""}! ${"\u{1F64F}"} Recebemos sua contribuição de ${brl(c.valor)} para "${c.meta_referente}". Segue o seu recibo nº ${r.data.numero}:\n${link}\n\nDeus abençoe!`;
      const numero = (c.whatsapp || "").replace(/\D/g, "");
      const dest = numero ? (numero.startsWith("55") ? numero : `55${numero}`) : "";
      window.open(`https://wa.me/${dest}?text=${encodeURIComponent(texto)}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError(e, "Não foi possível gerar o link do recibo."));
    }
  };


  const setStatus = async (c, status) => {
    setActing(c.id);
    try {
      await api.patch(`/admin/contribuicoes/${c.id}`, { status });
      toast.success(status === "confirmado" ? "Pagamento confirmado! Valor somado à meta." : "Contribuição recusada.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(null);
    }
  };

  const deleteRejected = async (c) => {
    setActing(c.id);
    try {
      await api.delete(`/admin/contribuicoes/${c.id}`);
      toast.success("Contribuição recusada excluída da lista.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(null);
    }
  };

  const receiptUrl = receipt ? `${API}/files/${receipt}` : null;
  const receiptIsPdf = receipt?.toLowerCase().endsWith(".pdf");

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {["", "pendente", "confirmado", "recusado"].map((s) => (
          <button
            key={s || "todas"}
            data-testid={`filter-${s || "todas"}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${
              filter === s
                ? "bg-blue-800 text-white border-blue-800"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {s === "" ? "Todas" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500 text-lg">
          Nenhuma contribuição encontrada.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`contribution-row-${c.id}`}
              className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-heading text-lg font-bold text-slate-900">{c.nome}</p>
                    {c.anonimo && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">ANÔNIMO NO MURAL</span>}
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${STATUS_STYLE[c.status]}`}>
                      {STATUS_LABEL[c.status]}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    Meta: <span className="font-semibold text-slate-700">{c.meta_referente}</span> · {formatDateTime(c.created_at)}
                    {c.whatsapp && ` · WhatsApp: ${c.whatsapp}`}
                  </p>
                  <p className="mt-2 text-xl font-extrabold text-blue-900">{brl(c.valor)}</p>
                  {c.confirmado_em && (
                    <p data-testid={`confirmado-em-${c.id}`} className="text-xs text-emerald-700 font-semibold">Confirmado em {formatDateTime(c.confirmado_em)}</p>
                  )}
                  {c.pedido_oracao && (
                    <p className="mt-2 text-sm text-slate-600 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 inline-flex items-start gap-2">
                      <HandHeart className="w-4 h-4 mt-0.5 shrink-0 text-blue-700" />
                      <span><span className="font-semibold">Pedido de oração:</span> {c.pedido_oracao}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0 lg:flex-col lg:items-stretch">
                  {c.status === "confirmado" && (
                    <>
                      <a
                        data-testid={`recibo-view-${c.id}`}
                        href={`${API}/admin/contribuicoes/${c.id}/recibo.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-900 font-bold text-sm border border-blue-200 transition-colors"
                      >
                        <Receipt className="w-4 h-4" /> Recibo PDF
                      </a>
                      <button
                        data-testid={`recibo-whatsapp-${c.id}`}
                        onClick={() => enviarRecibo(c)}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#1fbe5a] text-white font-bold text-sm transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" /> Enviar no WhatsApp
                      </button>
                    </>
                  )}
                  {c.comprovante && (
                    <button
                      data-testid={`receipt-view-${c.id}`}
                      onClick={() => setReceipt(c.comprovante)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm border border-slate-200 transition-colors"
                    >
                      <FileText className="w-4 h-4" /> Visualizar comprovante
                    </button>
                  )}
                  {c.status !== "confirmado" && (
                    <button
                      data-testid={`confirm-contribution-${c.id}`}
                      onClick={() => setStatus(c, "confirmado")}
                      disabled={acting === c.id}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors disabled:opacity-60"
                    >
                      {acting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Confirmar pagamento
                    </button>
                  )}
                  {c.status !== "recusado" && (
                    <button
                      data-testid={`reject-contribution-${c.id}`}
                      onClick={() => setStatus(c, "recusado")}
                      disabled={acting === c.id}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-sm border border-rose-200 transition-colors disabled:opacity-60"
                    >
                      <X className="w-4 h-4" /> Recusar
                    </button>
                  )}
                  {c.status === "recusado" && (
                    <button
                      data-testid={`delete-rejected-contribution-${c.id}`}
                      onClick={() => deleteRejected(c)}
                      disabled={acting === c.id}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm transition-colors disabled:opacity-60"
                    >
                      {acting === c.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Excluir
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent data-testid="receipt-modal" className="max-w-2xl max-h-[94vh] overflow-y-auto rounded-2xl p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-slate-900 pr-8">Comprovante</DialogTitle>
          </DialogHeader>
          {receiptIsPdf ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-blue-800 mx-auto" />
              <p className="mt-3 text-slate-600">O comprovante é um arquivo PDF.</p>
              <a
                data-testid="receipt-pdf-link"
                href={receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-800 hover:bg-blue-700 text-white font-bold transition-colors"
              >
                Abrir PDF em nova aba
              </a>
            </div>
          ) : (
            receiptUrl && (
              <img data-testid="receipt-image" src={receiptUrl} alt="Comprovante" className="w-full rounded-xl border border-slate-200" />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
